<template>
  <p>{{ $t("节点配置") }}</p>
  <t-form-item name="url" :rules="urlRule">
    <template #label>
      <text-with-mark text="URL" />
    </template>
    <t-input v-model="formData.url" maxlength="200" placeholder="https://" />
  </t-form-item>

  <t-form-item name="callbackTimeout" :rules="callbackTimeoutRule">
    <template #label>
      <text-with-mark :text="$t('超时时间')" />
      <span style="color: #99a2b2; font-size: 12px; margin-left: 8px">
        ({{ $t("毫秒，默认4500ms") }})
      </span>
      <t-popup
        :content="
          $t(
            '超时时间设置过长可能使该节点处理耗时增加，进而拖慢用户消息的响应速度，影响用户体验，请合理配置。',
          )
        "
        show-arrow
        destroy-on-close
      >
        <InfoCircleIcon class="infoIcon" />
      </t-popup>
    </template>
    <t-input v-model="callbackTimeoutModel" :placeholder="$t('请输入')" />
  </t-form-item>

  <t-form-item name="callbackRetryCount" :rules="callbackRetryCountRule">
    <template #label>
      <text-with-mark :text="$t('重试次数')" />
      <span style="color: #99a2b2; font-size: 12px; margin-left: 8px">
        ({{ $t("次，默认3次") }})
      </span>
    </template>
    <t-input v-model="callbackRetryCountModel" :placeholder="$t('请输入')" />
  </t-form-item>

  <div class="label">
    <div style="margin-bottom: 8px">
      {{ $t("鉴权") }}
      <t-popup
        :content="$t('用于验证调用者身份有效性并校验其访问权限')"
        show-arrow
        destroy-on-close
      >
        <InfoCircleIcon class="infoIcon" />
      </t-popup>
    </div>
    <t-switch :value="!!formData.auth.authType" @change="onAuthSwitchChange" />
  </div>

  <div v-if="!!formData.auth.authType">
    <p style="margin-top: 8px; margin-bottom: 8px">
      <text-with-mark :text="$t('鉴权类型')" />
    </p>
    <t-form-item name="type" style="margin-bottom: 0px">
      <t-radio :checked="isBearerAuth" @change="handleBearerAuthChange">
        Bearer
      </t-radio>
      <t-radio
        :checked="isCustomAuth"
        style="margin-left: 16px"
        @change="handleCustomAuthChange"
      >
        {{ $t("自定义") }}
      </t-radio>
    </t-form-item>

    <div v-if="formData.auth.authType === 2">
      <p style="margin-top: 8px; margin-bottom: 8px">
        <text-with-mark text="Token" />
      </p>
      <t-form-item
        name="auth.bearerAuth.bearerToken"
        :rules="tokenRule"
        style="margin-bottom: 12px"
      >
        <t-input
          v-model="formData.auth.bearerAuth.bearerToken"
          maxlength="256"
          :placeholder="$t('请输入')"
        />
      </t-form-item>
    </div>

    <div v-if="formData.auth.authType !== 2">
      <p style="margin-top: 8px; margin-bottom: 8px">
        <text-with-mark :text="$t('密钥位置')" />
      </p>
      <t-radio-group
        :value="`${formData.auth.authType}`"
        style="margin-bottom: 4px"
        @change="onAuthTypeChange"
      >
        <t-radio value="3">Header</t-radio>
        <t-radio value="4">Query</t-radio>
      </t-radio-group>

      <p style="margin-top: 8px; margin-bottom: 8px">
        <text-with-mark text="Key" />
      </p>
      <t-form-item
        name="auth.customAuth.key"
        :rules="keyRule"
        style="margin-bottom: 12px"
      >
        <t-input
          v-model="formData.auth.customAuth.key"
          maxlength="1024"
          :placeholder="$t('请输入')"
        />
      </t-form-item>
      <p style="margin-top: 8px; margin-bottom: 8px">
        <text-with-mark text="Value" />
      </p>
      <t-form-item
        name="auth.customAuth.value"
        :rules="requireRule"
        style="margin-bottom: 24px"
      >
        <t-input
          v-model="formData.auth.customAuth.value"
          maxlength="1024"
          :placeholder="$t('请输入')"
        />
      </t-form-item>
    </div>
  </div>

  <p>{{ $t("输入参数") }}</p>

  <div>
    <t-table
      v-if="(formData.request?.body ?? []).length > 0"
      class="no-border-table"
      :data="formData.request?.body"
      lazy-load
      size="small"
      :columns="columns"
      vertical-align="top"
    >
      <template #varType="{ rowIndex }">
        <t-form-item :name="`formData.request.body[${rowIndex}].varType`">
          <t-select
            :value="(formData.request?.body ?? [])[rowIndex].varType"
            :options="typeOptions"
            style="width: 150px"
            :placeholder="$t('请选择')"
            @change="onTypeChange(rowIndex, $event)"
          />
        </t-form-item>
      </template>
      <template #key="{ rowIndex, row }">
        <t-form-item :name="`formData.request.body[${rowIndex}].key`">
          <t-select
            v-if="row.varType === 1"
            v-model="(formData.request?.body ?? [])[rowIndex].key"
          >
            <t-option
              v-for="item in systemVarsPool"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            >
              <t-tooltip :content="item.tip">{{ item.label }}</t-tooltip>
            </t-option>
          </t-select>
          <select-with-operate
            v-if="row.varType === 2"
            v-model="(formData.request?.body ?? [])[rowIndex].key"
            :include-input-variables="true"
          />
          <div v-else-if="row.varType === 3" class="fixedVar">
            <t-input
              v-model="(formData.request?.body ?? [])[rowIndex].key"
              maxlength="200"
              placeholder="key"
              style="width: 105px"
            />
            <t-input
              v-model="(formData.request?.body ?? [])[rowIndex].value"
              maxlength="200"
              placeholder="value"
              style="width: 105px"
            />
          </div>
        </t-form-item>
      </template>
      <template #operation="{ rowIndex }">
        <CloseCircleIcon
          size="16"
          style="cursor: pointer; vertical-align: bottom"
          @click="deleteInputParam(rowIndex)"
        />
      </template>
    </t-table>
    <t-form-item :status-icon="false">
      <div v-if="(formData.request?.body?.length ?? 0) < 20">
        <t-link theme="primary" hover="color" @click="addInputParam">
          {{ $t("添加输入参数") }}
        </t-link>
      </div>
    </t-form-item>
  </div>

  <t-divider style="margin: 8px 0"></t-divider>

  <p style="margin-top: 24px; margin-bottom: 8px">{{ $t("输出参数") }}</p>
  <variable-list-editor
    :model-value="formData.response?.body ?? []"
    value-field="key"
    form-item-name-prefix="formData.response.body"
    :title="$t('参数')"
    :add-text="$t('添加输出参数')"
    :create-item="createOutputParam"
    :include-input-variables="false"
    link-wrapper-style="margin-top: 10px"
    @update:modelValue="updateOutputParams"
  />

  <div class="label">
    <div style="margin: 14px 0">
      <span>{{ $t("异常处理") }}</span>
      <t-popup
        :content="
          $t(
            '开启后，接口调用节点将新增异常处理分支。若调用失败，任务流自动进入该分支。',
          )
        "
        show-arrow
        destroy-on-close
      >
        <InfoCircleIcon class="infoIcon" />
      </t-popup>
    </div>
    <t-switch :value="!!formData.fallbackSwitch" @change="onSwitchChange" />
  </div>
</template>

<script lang="ts" setup>
import { computed, reactive, watch } from "vue";
import { CloseCircleIcon, InfoCircleIcon } from "tdesign-icons-vue-next";
import { trim } from "lodash-es";
import { systemVarsPool } from "@/utils/const";
import {
  validateBearerToken,
  validateHeaderKey,
  validateQueryKey,
  validateUrl,
} from "@/utils/rules";
import { t } from "@/utils/i18n";
import { getRelationUUID } from "@/utils";
import { FALLBACK_ERROR, FALLBACK_SUCCESS } from "./const";
import VariableListEditor from "./variable-list-editor.vue";

const emit = defineEmits(["updateData"]);

type NodeDataType = {
  request?: {
    body: Array<{
      varType: number; // 1=系统变量, 2=自定义变量, 3=固定值
      key: string;
      value?: string;
      valueType: string;
    }>;
  };
  response?: {
    body: Array<{
      key: string;
      valueType: string;
    }>;
  };
};

const props = defineProps<{
  data: {
    url: string;
    callbackTimeout?: number | string;
    callbackRetryCount?: number | string;
    fallbackSwitch?: number;
    branches?: Array<{ content: string; id: string; type: 0 | 1 }>;
    auth: {
      authType: number;
      bearerAuth: {
        bearerToken: string;
      };
      customAuth: {
        key: string;
        value: string;
      };
    };
  } & NodeDataType;
}>();

type Model = {
  nodeData: {
    url: string;
    callbackTimeout?: number | string;
    callbackRetryCount?: number | string;
    fallbackSwitch?: number;
    branches?: Array<{ content: string; id: string; type: 0 | 1 }>;
    auth: {
      authType: number;
      bearerAuth: {
        bearerToken?: string;
      };
      customAuth: {
        key: string;
        value: string;
      };
    };
  } & NodeDataType;
};

const formData = reactive<Model["nodeData"]>({
  request: props.data.request ?? {
    body: [
      {
        varType: 1,
        key: "",
        valueType: "string",
      },
    ],
  },
  response: props.data.response ?? {
    body: [{ key: "", valueType: "string" }],
  },
  branches: props.data.branches ?? [],
  url: props.data.url ?? "",
  callbackTimeout: props.data.callbackTimeout,
  callbackRetryCount: props.data.callbackRetryCount,
  fallbackSwitch: props.data.fallbackSwitch ?? 0,
  auth: props.data.auth ?? {
    authType: 0,
    bearerAuth: { bearerToken: "" },
    customAuth: { key: "", value: "" },
  },
});

const urlRule = [
  {
    validator: (url) => {
      return !!trim(url);
    },
    message: t("不能为空"),
    trigger: "change",
  },
  {
    validator: (url) => {
      return validateUrl(url);
    },
    message: t("请输入正确url地址"),
    trigger: "change",
  },
];

const requireRule = [
  {
    validator: (token) => {
      return !!trim(token);
    },
    message: t("不能为空"),
    trigger: "change",
  },
];

const tokenRule = [
  {
    validator: (token) => {
      return !!trim(token);
    },
    message: t("不能为空"),
    trigger: "change",
  },
  {
    validator: (token) => {
      return validateBearerToken(token);
    },
    message: t("格式错误"),
    trigger: "change",
  },
];

const keyRule = [
  {
    validator: (key) => {
      return !!trim(key);
    },
    message: t("不能为空"),
    trigger: "change",
  },
  {
    validator: (key) => {
      return formData.auth.authType === 3
        ? validateHeaderKey(key)
        : validateQueryKey(key);
    },
    message: t("格式错误"),
    trigger: "change",
  },
];

const validateOptionalNumber = (
  value: unknown,
  { min, max }: { min: number; max: number },
) => {
  if (value === null || value === undefined || value === "") {
    return true;
  }
  const numValue = Number(value);
  if (Number.isNaN(numValue)) {
    return false;
  }
  return Number.isInteger(numValue) && numValue >= min && numValue <= max;
};

const callbackTimeoutRule = [
  {
    validator: (value) => {
      return value !== null && value !== undefined && value !== "";
    },
    message: t("不能为空"),
    trigger: "change",
  },
  {
    validator: (value) => {
      return validateOptionalNumber(value, { min: 1, max: 20000 });
    },
    message: t("超时时间范围为1-20000毫秒"),
    trigger: "change",
  },
];

const callbackRetryCountRule = [
  {
    validator: (value) => {
      return value !== null && value !== undefined && value !== "";
    },
    message: t("不能为空"),
    trigger: "change",
  },
  {
    validator: (value) => {
      return validateOptionalNumber(value, { min: 0, max: 3 });
    },
    message: t("重试次数范围为0-3次"),
    trigger: "change",
  },
];

const normalizeOptionalNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const numValue = Number(value);
  return Number.isNaN(numValue) ? "" : numValue;
};

const callbackTimeoutModel = computed({
  get: () => formData.callbackTimeout ?? "",
  set: (value) => {
    formData.callbackTimeout = normalizeOptionalNumber(value);
  },
});

const callbackRetryCountModel = computed({
  get: () => formData.callbackRetryCount ?? "",
  set: (value) => {
    formData.callbackRetryCount = normalizeOptionalNumber(value);
  },
});

const onTypeChange = (index, value) => {
  const row = (formData.request?.body ?? [])[index];
  row.varType = value;
  row.key = "";
  row.value = "";
};

const onSwitchChange = (value) => {
  formData.fallbackSwitch = value ? 1 : 0;
  if (value) {
    formData.branches = [
      { id: getRelationUUID(), content: t("调用成功"), type: FALLBACK_SUCCESS },
      { id: getRelationUUID(), content: t("异常处理"), type: FALLBACK_ERROR },
    ];
  } else {
    formData.branches = [];
  }
};

const isBearerAuth = computed(() => formData.auth.authType === 2);
const isCustomAuth = computed(() => formData.auth.authType > 2);
const onAuthSwitchChange = (value) => {
  formData.auth.authType = value ? 2 : 0;
};

const onAuthTypeChange = (value) => {
  formData.auth.authType = +value;
};

const handleCustomAuthChange = (checked) => {
  formData.auth.authType = checked ? 3 : 2;
};
const handleBearerAuthChange = (checked) => {
  formData.auth.authType = checked ? 2 : 3;
};

const columns = [
  { colKey: "varType", title: t("参数类型"), width: 165 },
  { colKey: "key", title: t("参数") },
  { colKey: "operation", width: 30, align: "right" },
];

const typeOptions = [
  { value: 1, label: t("系统变量") },
  { value: 2, label: t("自定义变量") },
  { value: 3, label: t("固定值") },
];

const addInputParam = () => {
  formData.request?.body.push({
    varType: 1,
    key: "",
    value: "",
    valueType: "string",
  });
};

const deleteInputParam = (index: number) => {
  formData.request?.body.splice(index, 1);
};

const createOutputParam = () => ({
  key: "",
  valueType: "string",
});

const updateOutputParams = (body: Array<Record<string, any>>) => {
  if (!formData.response) formData.response = { body: [] };
  formData.response.body = body as Array<{ key: string; valueType: string }>;
};

watch(formData, (newVal) => {
  emit("updateData", newVal);
});
</script>
<style lang="less" scoped>
:deep(.flow-t-table td) {
  border: none;
}
:deep(.flow-t-table th) {
  border: none;
}
:deep(.flow-t-table.flow-t-size-s th) {
  padding-left: 0px;
  padding-top: 6px;
  padding-bottom: 6px;
}
:deep(.flow-t-table.flow-t-size-s td) {
  padding: 0px;
}
:deep(.flow-t-table tr) {
  height: 42px;
}
:deep(.flow-t-table .flow-t-table__header tr) {
  height: 30px;
}
.fixedVar {
  display: flex;
  flex: 1 1;
  justify-content: space-between;
}
.label {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.infoIcon {
  color: #99a2b2;
  margin-top: -3px;
  margin-left: 4px;
  vertical-align: middle;
}
</style>
